"use strict";

const strf = require("./string.format.js");

// MetaStreamIO models

const AppInfoModel = require("./models/event.appinfo.js");
const AppPerformanceModel = require("./models/event.appperformance.js");
const DeviceWebModel = require("./models/event.device_web.js");
const NetworkInfoModel = require("./models/event.network.js");
const UAParser = require("ua-parser-js");

/*
 *
 * Environment class
 *
 */

class MetaStreamIOEnvironment {
  constructor({ constants, logger, date, _enum } = {}) {
    this.constant = constants;
    this.date = date;
    this.logger = logger;
    this.app_info = new AppInfoModel();
    this.app_performance = new AppPerformanceModel();
    this.device = new DeviceWebModel();
    this.network_info = new NetworkInfoModel();
    this.ua_parser = new UAParser();
  }

  reset() {
    this.app_info = new AppInfoModel();
    this.app_performance = new AppPerformanceModel();
    this.device = new DeviceWebModel();
    this.network_info = new NetworkInfoModel();
  }

  async _getAppInfo() {
    return await this.logAppInfo()
      .then((appInfo) => {
        return Promise.resolve(appInfo);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  getAppInfo() {
    return this.logAppInfo();
  }

  async logAppInfo() {
    let app_id = null;
    let first_installation_time = null;
    let installation_id = null;
    let install_referrer = null;
    let last_update_time = null;
    let version = null;

    this.app_info = new AppInfoModel({
      first_installation_time: first_installation_time,
      id: app_id,
      installation_id: installation_id,
      install_referrer: install_referrer,
      last_update_time: last_update_time,
      version: version,
    });

    this.logger.log(
      this.constant.MetaStreamIO_Logger_Category_Environment,
      this.constant.MetaStreamIO_Environment_AppInfo.format(
        JSON.stringify(this.app_info)
      )
    );

    return this.app_info.json();
  }

  async getAppPerformance() {
    return await this.logAppPerformance();
  }

  async logAppPerformance() {
    let battery_level = null;
    let battery_low_power_mode = null;
    let battery_is_charging = null;
    let battery_seconds_to_charged = null;
    let battery_seconds_to_discharge = null;
    let data_down_kb = null;
    let data_up_kb = null;

    try {
      await navigator
        .getBattery()
        .then((battery) => {
          battery_level = battery.level;
          battery_seconds_to_charged = battery.chargingTime;
          battery_seconds_to_discharge = battery.dischargingTime;
          battery_is_charging = battery.charging;
        })
        .catch((err) => {});
    } catch (err) {
      this.logger.log("environment", "battery issues", JSON.stringify(err));
    }

    this.app_performance = new AppPerformanceModel({
      battery_low_power_mode: battery_low_power_mode,
      battery_percentage: battery_level,
      battery_seconds_to_charged: battery_seconds_to_charged,
      battery_seconds_to_discharge: battery_seconds_to_discharge,
      battery_is_charging: battery_is_charging,
      data_down_kb: data_down_kb,
      data_up_kb: data_up_kb,
    });

    this.logger.log(
      this.constant.MetaStreamIO_Logger_Category_Environment,
      this.constant.MetaStreamIO_Environment_AppPerformance.format(
        JSON.stringify(this.app_performance)
      )
    );

    return Promise.resolve(this.app_performance.json());
  }

  async getDevice() {
    return await this.logDevice()
      .then((device) => {
        return Promise.resolve(device);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  logDevice() {
    let _browser = this.ua_parser.getBrowser();
    let _device = this.ua_parser.getDevice();
    let _engine = this.ua_parser.getEngine();
    let _os = this.ua_parser.getOS();
    let _cpu = this.ua_parser.getCPU();
    let timezone_offset_sec;

    try {
      timezone_offset_sec = this.date.getDeviceTimezoneOffset();
    } catch {
      timezone_offset_sec = null;
    }

    this.device = new DeviceWebModel({
      brand: _device.vendor,
      browser: _browser.name,
      browser_version: _browser.version,
      device_type: _device.type,
      engine: _engine.name,
      engine_version: _engine.version,
      model: _device.model,
      operating_system: _os.name,
      operating_system_version: _os.version,
      screen_height: screen.height,
      screen_width: screen.width,
      supported_architectures: _cpu.architecture,
      timezone_offset_sec: timezone_offset_sec,
    });

    this.logger.log(
      this.constant.MetaStreamIO_Logger_Category_Environment,
      this.constant.MetaStreamIO_Environment_Device.format(
        JSON.stringify(this.device)
      )
    );

    return Promise.resolve(this.device);
  }

  async getNetworkInfo() {
    return await this.logNetworkInfo()
      .then((networkInfo) => {
        return Promise.resolve(networkInfo);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  async logNetworkInfo() {
    let networkInformation;
    let _downlink = null;
    let _downlinkMax = null;
    let _effectiveType = null;
    let _type = null;
    let _saveData = null;

    try {
      networkInformation = navigator.connection;
    } catch (err) {
      networkInformation = null;
    }

    if (networkInformation !== null) {
      _downlink = networkInformation.downlink
        ? networkInformation.downlink
        : null;
      _downlinkMax = networkInformation.downlinkMax
        ? networkInformation.downlinkMax
        : null;
      _effectiveType = networkInformation.effectiveType
        ? networkInformation.effectiveType
        : null;
      _saveData = networkInformation.saveData
        ? networkInformation.saveData
        : null;
      _type = networkInformation.type ? networkInformation.type : null;
    }

    this.logger.log(
      this.constant.MetaStreamIO_Logger_Category_Environment,
      this.constant.MetaStreamIO_Environment_Network.format(
        JSON.stringify(this.network_info)
      )
    );

    this.network_info = new NetworkInfoModel({
      cellular_generation: _effectiveType,
      downlink_speed: _downlink,
      downlink_speed_max: _downlinkMax,
      network_state_type: _type,
      save_data_mode: _saveData,
    });

    return Promise.resolve(this.network_info.json());
  }
}

module.exports = MetaStreamIOEnvironment;
